/**
 * BehaviourCard Component
 * Displays and manages behaviour settings for a channel
 */

import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { TONE_OPTIONS, ANSWER_LENGTH_OPTIONS, DEFAULT_BEHAVIOUR_PLACEHOLDER } from '../constants';
import { CHANNEL_ICONS } from '@/modules/behaviour/constants';
import { useUpdateChannel } from '@/hooks/use-channels';
import { toast } from '@/components/ui/sonner';
import { useReadOnly } from '@/lib/read-only-context';
import type { Channel } from '@/types';
import type { ToneOption, AnswerLengthOption, RadioTabOption } from '../types';
import { Hash } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

interface BehaviourCardProps {
  channel: Channel;
}

// Option button group component
interface OptionButtonGroupProps {
  label: string;
  options: RadioTabOption[];
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
}

const OptionButtonGroup = ({ label, options, value, onValueChange, disabled }: OptionButtonGroupProps) => {
  return (
    <div className='flex flex-col gap-2'>
      <label className='text-xs text-gray-700'>{label}</label>
      <div className='flex flex-wrap gap-2 items-center'>
        {options.map((option) => {
          const Icon = option.icon;
          const isActive = value === option.value;
          return (
            <Tooltip key={option.value}>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={disabled}
                  onClick={() => onValueChange(option.value)}
                  className={cn(
                    'gap-2 transition-all border-border text-gray-700 text-xs rounded-md',
                    isActive ? 'text-black-800 hover:bg-transparent' : 'hover:bg-black-800/10'
                  )}
                >
                  {Icon && (typeof Icon === 'string' ? <img src={Icon} alt="" className={cn('h-4 w-4', isActive && 'filter-[brightness(0)_saturate(100%)]')} /> : <Icon className={cn('h-4 w-4', isActive && 'text-black-800')} />)}
                  {option.label}
                </Button>
              </TooltipTrigger>
              {option.description && (
                <TooltipContent className='text-[10px] rounded-lg w-40'>
                  {option.description}
                </TooltipContent>
              )}
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export const BehaviourCard = ({ channel }: BehaviourCardProps) => {
  const [tone, setTone] = useState<ToneOption>((channel.tone.charAt(0).toUpperCase() + channel.tone.slice(1)) as ToneOption);
  const [answerLength, setAnswerLength] = useState<AnswerLengthOption>(channel.answerLength as AnswerLengthOption);
  const [behaviour, setBehaviour] = useState<string>(channel.behaviour || '');

  const updateChannelMutation = useUpdateChannel();
  const readOnly = useReadOnly();
  const disabled = readOnly || updateChannelMutation.isPending;

  // Update local state when channel prop changes
  useEffect(() => {
    setTone(channel.tone as ToneOption);
    setAnswerLength(channel.answerLength as AnswerLengthOption);
    setBehaviour(channel.behaviour || '');
  }, [channel]);

  const handleUpdate = async (field: 'tone' | 'answerLength' | 'behaviour', value: string) => {
    try {
      await updateChannelMutation.mutateAsync({
        id: channel.id,
        data: {
          [field]: value
        }
      });
      toast.success('Channel updated successfully');
    } catch (error) {
      toast.error('Failed to update channel');
      console.error('Error updating channel:', error);
    }
  };

  const handleToneChange = (newTone: string) => {
    setTone(newTone as ToneOption);
    handleUpdate('tone', newTone);
  };

  const handleAnswerLengthChange = (newLength: string) => {
    setAnswerLength(newLength as AnswerLengthOption);
    handleUpdate('answerLength', newLength);
  };

  const handleBehaviourBlur = () => {
    if (behaviour !== channel.behaviour) {
      handleUpdate('behaviour', behaviour);
    }
  };

  // Get icon for channel
  const getChannelIcon = () => {
    const channelType = channel.name.toLowerCase() as keyof typeof CHANNEL_ICONS;
    if (CHANNEL_ICONS[channelType]) {
      return <img src={CHANNEL_ICONS[channelType].active} alt={channel.name} className='' />;
    }
    return <Hash className='w-4 h-4 text-muted-foreground' />;
  };

  return (
    <div className='flex flex-col gap-3'>
      {/* Channel Name with Icon */}

      {/* Card */}
      <Card className='p-4 flex flex-col gap-4 rounded-3xl'>
        <div className='flex items-center gap-1'>
          {getChannelIcon()}
          <h3 className='text-sm font-semibold capitalize'>{channel.name.toLowerCase() === 'sms' ? channel.name.toUpperCase() : channel.name}</h3>
        </div>

        {/* First Row: Tone and Answer Length */}
        {/* <div className='grid grid-cols-1 md:grid-cols-1 gap-4'> */}
        <OptionButtonGroup
          label='Tone of voice'
          options={TONE_OPTIONS}
          value={tone}
          onValueChange={handleToneChange}
          disabled={disabled}
        />
        <OptionButtonGroup
          label='Answer Length'
          options={ANSWER_LENGTH_OPTIONS}
          value={answerLength}
          onValueChange={handleAnswerLengthChange}
          disabled={disabled}
        />
        {/* </div> */}

        {/* Second Row: Behaviour Textarea */}
        <div className='flex flex-col gap-2'>
          <label htmlFor={`behaviour-${channel.id}`} className='text-xs text-gray-700'>
            Communication Style
          </label>
          <Textarea
            id={`behaviour-${channel.id}`}
            value={behaviour}
            onChange={(e) => setBehaviour(e.target.value)}
            onBlur={handleBehaviourBlur}
            placeholder={DEFAULT_BEHAVIOUR_PLACEHOLDER}
            disabled={disabled}
            readOnly={readOnly}
            className={cn(
              'h-[115px] resize-none rounded-xl bg-gray-50 border border-border text-xs md:text-xs placeholder:text-xs placeholder:italic',
              disabled && 'opacity-50'
            )}
          />
        </div>
      </Card>
    </div>
  );
};
